import {
    Component,
    ojs,
    app,
    state,
    v,
    each,
    State,
} from "modular-openscriptjs";
import { monetary, either, cleanDispose, isMobile, getCommissionRatesForPrice } from "../../helpers";
import SearchResultProductView from "./SearchResultProductView";
import { tool } from "../../fotastart";
import { productWhatsappHref } from "../../shareLink";
import ViewProductCanvas from "../Offcanvas/ViewProductCanvas";
import ViewProductModal from "../Modals/ViewProductModal";

const h = app("h");
const dc = app("dc");

ojs(SearchResultProductView, ViewProductCanvas, ViewProductModal);

export default class ViewProduct extends Component {
    holder = null;

    constructor() {
        super();
        this.activeImage = state("");
    }

    render(productState, holder) {
        const product =
            productState instanceof State ? productState.value : productState;
        if (!product?.id) return h.empty();

        if (this.activeImage.value === "" && product.images?.length > 0) {
            this.activeImage.value = product.images[0].path;
        } else if (this.activeImage.value === "" && product.thumbnail_path) {
            this.activeImage.value = product.thumbnail_path;
        }

        const userCurrency = dc.userCurrency;

        // The most recently updated currently-active deal on this product,
        // if any - mirrors Product::currentDeal()'s tie-break server-side.
        // product.discounted_price (an appended field on every Product
        // payload) is the authoritative discounted price already computed
        // server-side, so it's used directly rather than re-deriving it
        // from the deal's raw type/value here.
        const activeDeal = (product.deals || [])
            .filter((deal) => {
                if (!deal.active) return false;
                const now = new Date();
                if (deal.starts_at && new Date(deal.starts_at) > now)
                    return false;
                if (deal.ends_at && new Date(deal.ends_at) < now)
                    return false;
                return true;
            })
            .sort(
                (a, b) => new Date(b.updated_at) - new Date(a.updated_at),
            )[0];

        const normalPrice = parseFloat(
            product.final_price || product.selling_price || product.price || 0,
        );
        const discountedPrice = parseFloat(product.discounted_price);
        const hasDiscount =
            activeDeal &&
            !isNaN(discountedPrice) &&
            discountedPrice < normalPrice;

        const discountPercent = hasDiscount
            ? Math.round(((normalPrice - discountedPrice) / normalPrice) * 100)
            : 0;

        return h.div(
            { class: "view-product-container" },

            // Main Product Section
            // Image Section
            h.div(
                { class: "col-12 col-md-6" },
                either(product.images.length > 0, () => [
                    h.div(
                        {
                            class: "rounded-4 overflow-hidden shadow-sm mb-3 bg-light",
                        },
                        v(
                            this.activeImage,
                            (img) =>
                                h.img({
                                    src: img,
                                    class: "img-fluid w-100",
                                    style: "object-fit: contain;",
                                    alt: product.name,
                                }),

                            {
                                class: "d-block",
                                c_attr: {
                                    class: "d-block",
                                },
                            },
                        ),
                    ),
                    // Thumbnails
                    either(product.images.length > 1, () =>
                        h.div(
                            {
                                class: "d-flex gap-2 overflow-x-auto pb-2 px-4 scrollbar-hidden",
                                style: "scrollbar-width: none;",
                            },
                            each(product.images, (img) =>
                                h.div(
                                    {
                                        class: "rounded-3 overflow-hidden border cursor-pointer flex-shrink-0",
                                        style: v(
                                            this.activeImage,
                                            (active) =>
                                                `width: 70px; height: 70px; border-width: 2px !important; border-color: ${active === img.path ? "#0d6efd" : "#dee2e6"} !important; transition: all 0.2s;`,
                                        ),
                                        listeners: {
                                            click: () =>
                                                (this.activeImage.value =
                                                    img.path),
                                        },
                                    },
                                    h.img({
                                        src: img.path,
                                        class: "w-100 h-100 object-fit-cover",
                                    }),
                                ),
                            ),
                        ),
                    ),
                ]),
            ),

            h.div(
                {
                    class: "col-12 col-md-6 bg-white p-4 mb-4 border-0 rounded-4",
                },
                h.div(
                    { class: "row g-4" },

                    // Content Section
                    h.div(
                        { class: "col-12 d-flex flex-column" },
                        h.h2({ class: "fw-bold mb-1 text-dark" }, product.name),

                        either(
                            hasDiscount,
                            () => [
                                h.div(
                                    {
                                        class: "d-flex align-items-center flex-wrap gap-2 mb-1",
                                    },
                                    h.span(
                                        {
                                            class: "text-muted text-decoration-line-through fs-6",
                                        },
                                        monetary(normalPrice, userCurrency),
                                    ),
                                    h.h3(
                                        { class: "text-success fw-bold mb-0" },
                                        monetary(discountedPrice, userCurrency),
                                    ),
                                    h.span(
                                        {
                                            class: "badge bg-success-subtle text-success rounded-pill px-2 py-1 fw-bold d-inline-flex align-items-center gap-1",
                                        },
                                        h.i({ class: "bi bi-tag-fill" }),
                                        `-${discountPercent}%`,
                                    ),
                                ),
                                h.div(
                                    {
                                        class: "text-success small fw-semibold mb-2 d-flex align-items-center gap-1",
                                    },
                                    h.i({ class: "bi bi-tag-fill" }),
                                    `Deal: ${activeDeal.name}`,
                                ),
                            ],
                            () =>
                                h.h3(
                                    { class: "text-dark fw-bold mb-2" },
                                    monetary(normalPrice, userCurrency),
                                ),
                        ),

                        // Price transparency note: always shown - the price above already
                        // has Carata's commission markup baked in.
                        h.span(
                            {
                                class: "text-muted d-block mb-2",
                                style: "font-size: 0.75rem;",
                            },
                            "(price includes Carata's markup)",
                        ),

                        either(!(product.package_price > 0), null, () =>
                            h.div(
                                {
                                    class: "d-flex align-items-center flex-wrap gap-1 mb-2",
                                },
                                h.span(
                                    { class: "text-dark fw-semibold small" },
                                    `+ ${monetary(product.package_price, userCurrency)} for packaging`,
                                ),
                            ),
                        ),

                        // Share on WhatsApp: below the price, text + link
                        // (https://app.carata.co/products/{slug}) for customers.
                        h.call(() => {
                            const href = productWhatsappHref(product);
                            if (!href) return null;

                            return h.a(
                                {
                                    href,
                                    target: "_blank",
                                    rel: "noopener",
                                    class: "btn rounded-pill px-4 py-2 fw-bold shadow-sm d-inline-flex align-items-center gap-2 text-white mb-3",
                                    style: "background-color: #25D366; border-color: #25D366;",
                                },
                                h.i({ class: "bi bi-whatsapp fs-5" }),
                                "Share on WhatsApp",
                            );
                        }),

                        // Business-only breakdown: what you listed vs. what you'll
                        // actually be paid, since customers never see this split.
                        h.call(() => {
                            const basePrice = parseFloat(
                                product.selling_price ?? product.price ?? 0,
                            );
                            if (basePrice <= 0) return null;

                            const packagePrice = parseFloat(
                                product.package_price || 0,
                            );
                            const { businessRate } =
                                getCommissionRatesForPrice(basePrice);
                            const payout =
                                basePrice -
                                Math.ceil((basePrice * businessRate) / 100) +
                                packagePrice;

                            return h.div(
                                {
                                    class: "p-3 bg-light rounded-3 mb-3 border border-light-subtle",
                                },
                                h.div(
                                    {
                                        class: "text-muted small fw-semibold text-uppercase mb-2",
                                        style: "font-size: 0.7rem; letter-spacing: 0.5px;",
                                    },
                                    "Your Numbers (not shown to customers)",
                                ),
                                h.div(
                                    {
                                        class: "d-flex justify-content-between small mb-1",
                                    },
                                    h.span({ class: "text-muted" }, "Your listed price"),
                                    h.span(
                                        { class: "fw-semibold text-dark" },
                                        monetary(basePrice, userCurrency),
                                    ),
                                ),
                                either(!(packagePrice > 0), null, () =>
                                    h.div(
                                        {
                                            class: "d-flex justify-content-between small mb-1",
                                        },
                                        h.span(
                                            { class: "text-muted" },
                                            "Packaging fee (not commissioned)",
                                        ),
                                        h.span(
                                            { class: "fw-semibold text-dark" },
                                            monetary(packagePrice, userCurrency),
                                        ),
                                    ),
                                ),
                                h.div(
                                    {
                                        class: "d-flex justify-content-between small pt-2 mt-1 border-top",
                                    },
                                    h.span(
                                        { class: "fw-bold text-dark" },
                                        "You'll receive",
                                    ),
                                    h.span(
                                        { class: "fw-bold text-primary" },
                                        monetary(payout, userCurrency),
                                    ),
                                ),
                            );
                        }),

                        // Description (Now below Price)
                        h.p(
                            {
                                class: "text-muted mb-4",
                                style: "font-size: 0.95rem; line-height: 1.6;",
                            },
                            product.short_description,
                        ),

                        // Features (Vertical List)
                        h.div(
                            { class: "features-list mb-4" },
                            each(product.features, (feature) =>
                                h.div(
                                    { class: "mb-4 pb-3" },
                                    h.div(
                                        {
                                            class: "d-flex justify-content-between align-items-center mb-1 flex-wrap",
                                        },
                                        h.span(
                                            { class: "fw-bold text-dark fs-5" },
                                            feature.name,
                                        ),
                                        h.span(
                                            { class: "badge bg-secondary bg-opacity-10 text-secondary border border-secondary border-opacity-25 rounded-pill px-3 py-1 fw-medium" },
                                            h.call(() => {
                                                switch (feature.choice_type) {
                                                    case "single_choice":
                                                        return "Single Choice";
                                                    case "multiple_choice":
                                                        return "Multiple Choice";
                                                    case "text":
                                                        return "Free Text";
                                                    case "boolean":
                                                        return "Yes/No";
                                                    case "number":
                                                        return "Number";
                                                    case "range":
                                                        return "Range";
                                                    default:
                                                        return "Unknown";
                                                }
                                            })
                                        )
                                    ),
                                    h.div(
                                        { class: "options-list d-flex flex-wrap gap-2 mt-3" },
                                        either(
                                            feature.choice_type === "number",
                                            () => [
                                                h.span(
                                                    { class: "badge bg-secondary bg-opacity-10 text-secondary border border-secondary border-opacity-25 rounded-pill flex-wrap px-3 py-2 fw-semibold d-flex align-items-center gap-2" },
                                                    h.span(
                                                        { class: "text-wrap" },
                                                        feature.value_label
                                                            ? `${feature.value_label}`
                                                            : "No placeholder set",
                                                    ),
                                                ),
                                                either(feature.has_different_prices, () =>
                                                    h.span(
                                                        { class: "badge bg-primary bg-opacity-10 text-primary border border-primary border-opacity-25 rounded-pill px-3 py-2 fw-semibold d-flex align-items-center gap-2" },
                                                        h.span({ class: "fs-6" }, "Rate:"),
                                                        h.span(
                                                            { class: "badge bg-white text-primary shadow-sm border border-primary border-opacity-25 rounded-pill px-2 py-1 ms-1" },
                                                            `Every ${feature.price_interval} = ${monetary(feature.price_per_interval, userCurrency)}`,
                                                        ),
                                                    ),
                                                ),
                                            ],
                                            () =>
                                                each(feature.values, (option) =>
                                                    h.span(
                                                        { class: "badge bg-primary bg-opacity-10 text-primary border border-primary border-opacity-25 rounded-pill flex-wrap px-3 py-2 fw-semibold d-flex align-items-center gap-2" },
                                                        h.span({ class: "fs-6" }, option.value),
                                                        either(feature.has_different_prices, () =>
                                                            h.span(
                                                                { class: "badge bg-white text-primary shadow-sm border border-primary border-opacity-25 rounded-pill px-2 py-1 ms-1" },
                                                                option.price > 0
                                                                    ? monetary(option.final_price || option.price, userCurrency)
                                                                    : monetary(product.final_price || product.selling_price || product.price, userCurrency)
                                                            )
                                                        )
                                                    )
                                                ),
                                        ),
                                    )
                                )
                            )
                        ),

                        // Extras (Vertical List left aligned)
                        either(product.extras.length > 0, () =>
                            h.div(
                                { class: "extras-list mb-4" },
                                h.label(
                                    { class: "fw-bold text-dark mb-3 d-block fs-5" },
                                    "Extras",
                                ),
                                each(product.extras, (extra) =>
                                    h.div(
                                        {
                                            class: "d-flex align-items-center justify-content-between p-3 mb-2 rounded-4 border border-secondary border-opacity-10 bg-white shadow-sm flex-wrap gap-1",
                                        },
                                        h.div(
                                            { class: "fw-semibold text-dark fs-6 d-flex align-items-center gap-3" },
                                            h.span(extra.name),
                                            h.span({ class: "badge bg-secondary bg-opacity-10 text-secondary border border-secondary border-opacity-25 rounded-pill px-3 py-1 fw-medium" }, `Qty: ${extra.quantity || 1}`)
                                        ),
                                        h.div(
                                            { class: "badge bg-success bg-opacity-10 text-success border border-success border-opacity-25 rounded-pill px-3 py-2 fw-bold fs-6" },
                                            `+ ${monetary(extra.price * (extra.quantity || 1), userCurrency)}`,
                                        )
                                    )
                                )
                            )
                        )
                    )
                )
            )
        );
    }

    async showProduct(product) {
        if (this.holder) {
            cleanDispose(this.holder);
        }

        if (isMobile()) {
            this.holder = tool.offcanvas(
                h.ViewProductCanvas(h.ViewProduct(product, "offcanvas"), {
                    class: "p-0 rounded-top-5",
                }),
            );
        } else {
            this.holder = tool.plainModal(
                h.ViewProductModal(h.ViewProduct(product, "modal")),
            );
        }

        this.holder.show();
    }

    closeProductView() {
        this.holder.hide();
    }

    cleanUp() {
        if (this.holder) {
            cleanDispose(this.holder);
        }
    }
}

ojs(ViewProduct);
